import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Users } from "lucide-react";

import { formatPilotageCurrency, formatPilotageCount } from "@/lib/pilotageUi";

const CHART_COLORS = [
  "#0EA5E9",
  "#14B8A6",
  "#22C55E",
  "#EAB308",
  "#F97316",
  "#F43F5E",
  "#8B5CF6",
  "#6366F1",
];

const RealizedRevenueByClient = ({ data, year }) => {
  if (!data || !data.by_client || data.by_client.length === 0) {
    return null;
  }

  const { by_client } = data;
  const forecastOnly = !data.period_end_label;
  const chartAmount = client => forecastOnly ? Number(client.annual_net_estimate || 0) : Number(client.amount || 0);
  const forecastTotal = by_client.reduce((sum, client) => sum + chartAmount(client), 0);

  const getMonthNameLower = (label) => {
    if (!label) return "N-1";
    const parts = label.split(" ");
    return parts[0].toLowerCase();
  };

  // Prepare chart data: top 5 clients, others grouped
  const topClientsCount = 5;
  const sortedClients = [...by_client].sort((a, b) => chartAmount(b) - chartAmount(a));
  
  const topClients = sortedClients.slice(0, topClientsCount);
  const otherClients = sortedClients.slice(topClientsCount);

  const chartData = topClients.map((client, index) => ({
    name: client.client_name,
    amount: chartAmount(client),
    color: CHART_COLORS[index % CHART_COLORS.length],
  }));

  if (otherClients.length > 0) {
    const otherAmount = otherClients.reduce((sum, c) => sum + chartAmount(c), 0);
    if (otherAmount > 0) {
      chartData.push({
        name: "Autres",
        amount: otherAmount,
        color: "#94A3B8",
      });
    }
  }

  return (
    <div className="grid min-w-0 grid-cols-1 gap-6 lg:grid-cols-3" data-testid="pilotage-realized-revenue-by-client-section">
      {/* Chart card */}
      <div className="lg:col-span-1 rounded-xl border border-white/10 bg-white/[0.02] p-5 flex flex-col">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-lenvia-blue" />
          <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white">
            {forecastOnly ? `Top clients (CA estimé ${year || ""})` : "Top Clients (CA Réalisé)"}
          </h3>
        </div>
        <div className="h-[200px] w-full min-w-0 flex-1">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              layout="vertical"
              margin={{ top: 10, right: 10, left: -10, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff15" horizontal={false} />
              <XAxis
                type="number"
                stroke="#94A3B8"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={(value) => `${Math.round(value / 1000)}k`}
              />
              <YAxis
                type="category"
                dataKey="name"
                stroke="#94A3B8"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                width={80}
                tickFormatter={(value) =>
                  value.length > 12 ? `${value.substring(0, 10)}...` : value
                }
              />
              <Tooltip
                cursor={{ fill: "rgba(255,255,255,0.05)" }}
                contentStyle={{
                  backgroundColor: "#0f172a",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "12px",
                }}
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const point = payload[0]?.payload;
                  return (
                     <div className="rounded-2xl border border-white/10 bg-slate-950/95 p-3 shadow-xl">
                      <p className="text-xs font-medium text-white">{point?.name}</p>
                      <p className="mt-1 text-xs text-slate-200">
                        {forecastOnly ? "CA estimé" : "CA réalisé"} : {formatPilotageCurrency(point?.amount)}
                      </p>
                    </div>
                  );
                }}
              />
              <Bar dataKey="amount" fill="#3B82F6" radius={[0, 4, 4, 0]} maxBarSize={20} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Table card */}
      <div className="lg:col-span-2 rounded-xl border border-white/10 bg-white/[0.02] p-5">
        <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-white mb-4">
          {forecastOnly ? `Prévisions par client — ${year || ""}` : "Détail du CA réalisé par client"}
        </h3>
        <div className="w-full max-h-[200px] overflow-y-auto pr-1">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="sticky top-0 bg-[#0f172a] border-b border-white/10 text-slate-400 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="py-2 font-semibold">Client</th>
                <th className="py-2 text-right font-semibold">{forecastOnly ? `CA réalisé ${year || ""}` : `CA réalisé à fin ${getMonthNameLower(data.period_end_label)}`}</th>
                <th className="py-2 text-right font-semibold">{forecastOnly ? "% prévisionnel" : "% total"}</th>
                <th className="py-2 text-right font-semibold">Missions</th>
                <th className="py-2 text-right font-semibold">CA annuel net estimé</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {sortedClients.map((client) => (
                <tr key={client.client_id || client.client_name} className="hover:bg-white/[0.02] transition-colors">
                  <td className="py-2.5 font-medium text-white truncate max-w-[180px]" title={client.client_name}>
                    {client.client_name}
                  </td>
                  <td className="py-2.5 text-right font-semibold tabular-nums text-emerald-400">
                    {formatPilotageCurrency(client.amount)}
                  </td>
                  <td className="py-2.5 text-right font-medium text-slate-400 tabular-nums">
                    {(forecastOnly ? (forecastTotal ? chartAmount(client) / forecastTotal * 100 : 0) : client.percentage || 0).toFixed(0)} %
                  </td>
                  <td className="py-2.5 text-right font-semibold tabular-nums text-slate-300">
                    {formatPilotageCount(client.missions_count)}
                  </td>
                  <td className="py-2.5 text-right font-semibold tabular-nums text-lenvia-blue">
                    {client.annual_net_estimate !== undefined && client.annual_net_estimate !== null
                      ? formatPilotageCurrency(client.annual_net_estimate)
                      : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RealizedRevenueByClient;
